/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Menu, X, Home, Calendar, UtensilsCrossed, Settings, LogOut, 
  Plus, Trash2, Save, Image, Check, XCircle, FileText, BookOpen, Users, Images,
  GripVertical, Edit2, FolderPlus, PartyPopper, Copy, ExternalLink, Truck, FileEdit, Download, Upload, ChevronDown, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ImageUpload from '../../components/ImageUpload';
import PageContentTabs from '../../components/PageContentTabs';
import ClientsTab from '../../components/ClientsTab';
import PopupEventsManager from '../../components/PopupEventsManager';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { admin, logout, token } = useAuth();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data states
  const [weeklyMenu, setWeeklyMenu] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [events, setEvents] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
  const [newCategory, setNewCategory] = useState({ slug: '', name_fr: '', name_en: '', name_pt: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', num_guests: 10, organizer_name: '', organizer_email: '', organizer_whatsapp: '', organizer_password: '', menu_id: '' });
  const [eventMenus, setEventMenus] = useState([]);
  const [showEventMenuForm, setShowEventMenuForm] = useState(false);
  const [editingEventMenu, setEditingEventMenu] = useState(null);
  const [eventMenuForm, setEventMenuForm] = useState({ name: '', items: [] });
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);

  // Bulk JSON import per category
  const [bulkImportCategory, setBulkImportCategory] = useState(null); // slug or null
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportSaving, setBulkImportSaving] = useState(false);

  // Weekly Menu JSON import
  const [weeklyJsonOpen, setWeeklyJsonOpen] = useState(false);
  const [weeklyJsonText, setWeeklyJsonText] = useState('');

  // Sidebar collapsible groups (open/closed state)
  const [sidebarGroups, setSidebarGroups] = useState({ content: false });

  // Menu form state
  const [menuForm, setMenuForm] = useState({
    week_start: new Date(),
    week_end: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
    price_full: 28.90,
    price_entree_plat: 24.90,
    price_plat_dessert: 23.90,
    price_plat_only: 17.90,
    dishes: []
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [menuRes, reservationsRes, menuItemsRes, categoriesRes, contentRes, settingsRes, adminsRes, galleryRes, eventsRes, eventMenusRes, deliveryOrdersRes] = await Promise.all([
        axios.get(`${API}/weekly-menu`),
        axios.get(`${API}/reservations`),
        axios.get(`${API}/menu-items`),
        axios.get(`${API}/menu-categories`),
        axios.get(`${API}/content`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/auth/admins`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/events`),
        axios.get(`${API}/event-menus`),
        axios.get(`${API}/delivery/orders`)
      ]);
      
      if (menuRes.data) {
        setWeeklyMenu(menuRes.data);
        setMenuForm({
          ...menuRes.data,
          week_start: new Date(menuRes.data.week_start),
          week_end: new Date(menuRes.data.week_end)
        });
      }
      
      setReservations(reservationsRes.data || []);
      setMenuItems(menuItemsRes.data || []);
      setMenuCategories(categoriesRes.data || []);
      setSiteContent(contentRes.data || {});
      setSiteSettings(settingsRes.data || {});
      setAdmins(adminsRes.data || []);
      setGalleryImages(galleryRes.data || []);
      setEvents(eventsRes.data || []);
      setEventMenus(eventMenusRes.data || []);
      setDeliveryOrders(deliveryOrdersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Weekly Menu Functions
  const addDish = (category) => {
    const newDish = {
      id: `dish-${Date.now()}`,
      name_fr: '', name_en: '', name_pt: '',
      description_fr: '', description_en: '', description_pt: '',
      category,
      image_url: ''
    };
    setMenuForm(prev => ({ ...prev, dishes: [...prev.dishes, newDish] }));
  };

  const updateDish = (dishId, field, value) => {
    setMenuForm(prev => ({
      ...prev,
      dishes: prev.dishes.map(d => d.id === dishId ? { ...d, [field]: value } : d)
    }));
  };

  const removeDish = (dishId) => {
    setMenuForm(prev => ({ ...prev, dishes: prev.dishes.filter(d => d.id !== dishId) }));
  };

  const applyWeeklyJsonImport = () => {
    let parsed;
    try {
      parsed = JSON.parse(weeklyJsonText);
    } catch (e) {
      toast.error('JSON inválido: ' + e.message);
      return;
    }
    const arr = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.dishes) ? parsed.dishes : null);
    if (!arr) {
      toast.error('Envie um array de pratos ou { "dishes": [...] }');
      return;
    }
    const validCats = new Set(['entree', 'meat', 'vegetarian', 'seafood', 'dessert']);
    const dishesToAdd = [];
    const skipped = [];
    arr.forEach((raw, idx) => {
      if (!raw || typeof raw !== 'object' || !validCats.has(raw.category)) {
        skipped.push(`#${idx + 1}: category inválida (use entree, meat, vegetarian, seafood ou dessert)`);
        return;
      }
      if (!raw.name_fr) {
        skipped.push(`#${idx + 1}: falta name_fr`);
        return;
      }
      dishesToAdd.push({
        id: `dish-${Date.now()}-${idx}`,
        category: raw.category,
        name_fr: raw.name_fr || '',
        name_en: raw.name_en || raw.name_fr || '',
        name_pt: raw.name_pt || raw.name_fr || '',
        description_fr: raw.description_fr || '',
        description_en: raw.description_en || '',
        description_pt: raw.description_pt || '',
        image_url: raw.image_url || ''
      });
    });
    setMenuForm(prev => ({ ...prev, dishes: [...prev.dishes, ...dishesToAdd] }));
    if (dishesToAdd.length) toast.success(`${dishesToAdd.length} pratos adicionados (clique em Enregistrer para salvar)`);
    if (skipped.length) {
      toast.error(`${skipped.length} ignorados (ver console)`);
      console.warn('Weekly JSON skipped:', skipped);
    }
    setWeeklyJsonOpen(false);
    setWeeklyJsonText('');
  };

  const saveWeeklyMenu = async () => {
    setSaving(true);
    try {
      const payload = {
        week_start: format(menuForm.week_start, 'yyyy-MM-dd'),
        week_end: format(menuForm.week_end, 'yyyy-MM-dd'),
        price_full: menuForm.price_full,
        price_entree_plat: menuForm.price_entree_plat,
        price_plat_dessert: menuForm.price_plat_dessert,
        price_plat_only: menuForm.price_plat_only,
        dishes: menuForm.dishes
      };

      if (weeklyMenu?.id) {
        await axios.put(`${API}/weekly-menu/${weeklyMenu.id}`, payload);
      } else {
        const response = await axios.post(`${API}/weekly-menu`, payload);
        setWeeklyMenu(response.data);
      }
      
      toast.success('Menu enregistré!');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Menu Items (Cardápio) Functions
  const addMenuItem = (category) => {
    const newItem = {
      id: `new-${Date.now()}`,
      category,
      name_fr: '', name_en: '', name_pt: '',
      description_fr: '', description_en: '', description_pt: '',
      price: 0,
      image_url: '',
      is_available: true,
      delivery_available: true,
      sort_order: menuItems.filter(i => i.category === category).length,
      isNew: true
    };
    setMenuItems(prev => [...prev, newItem]);
  };

  const updateMenuItem = (itemId, field, value) => {
    setMenuItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const saveMenuItem = async (item) => {
    setSaving(true);
    try {
      const payload = {
        category: item.category,
        subcategory: item.subcategory || '',
        name_fr: item.name_fr,
        name_en: item.name_en,
        name_pt: item.name_pt,
        description_fr: item.description_fr || '',
        description_en: item.description_en || '',
        description_pt: item.description_pt || '',
        price: parseFloat(item.price) || 0,
        image_url: item.image_url || '',
        is_available: item.is_available !== false,
        delivery_available: item.delivery_available !== false,
        sort_order: item.sort_order || 0
      };

      if (item.isNew) {
        await axios.post(`${API}/menu-items`, payload);
      } else {
        await axios.put(`${API}/menu-items/${item.id}`, payload);
      }
      
      toast.success('Item sauvegardé!');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteMenuItem = async (itemId, isNew) => {
    if (isNew) {
      setMenuItems(prev => prev.filter(i => i.id !== itemId));
      return;
    }
    if (!window.confirm('Supprimer cet item?')) return;
    try {
      await axios.delete(`${API}/menu-items/${itemId}`);
      toast.success('Item supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Menu Backup / Restore
  const exportMenuBackup = async () => {
    try {
      const response = await axios.get(`${API}/menu/backup`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `beni-menu-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Backup baixado (${response.data.counts?.items || 0} itens, ${response.data.counts?.categories || 0} categorias)`);
    } catch (error) {
      toast.error('Erro ao exportar backup');
    }
  };

  const importMenuBackup = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data.items) || !Array.isArray(data.categories)) {
        toast.error('Arquivo inválido (falta categories/items)');
        return;
      }
      const mode = window.confirm(
        `Restaurar ${data.items.length} itens e ${data.categories.length} categorias?\n\n` +
        `OK = MESCLAR (atualiza itens existentes pelo ID, mantém os demais)\n` +
        `Cancelar = SUBSTITUIR (apaga TUDO antes de importar)`
      ) ? 'merge' : 'replace';

      if (mode === 'replace' && !window.confirm('⚠️ Isso vai APAGAR todos os itens e categorias atuais. Confirmar?')) {
        return;
      }

      const response = await axios.post(`${API}/menu/restore`, {
        categories: data.categories,
        items: data.items,
        mode,
      });
      toast.success(`Restaurado: ${response.data.items_imported} itens, ${response.data.categories_imported} categorias`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao importar backup: ' + (error?.response?.data?.detail || error.message));
    }
  };

  const submitBulkImport = async () => {
    if (!bulkImportCategory) return;
    let parsed;
    try {
      parsed = JSON.parse(bulkImportText);
    } catch (e) {
      toast.error('JSON inválido: ' + e.message);
      return;
    }
    const items = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.items) ? parsed.items : null);
    if (!items) {
      toast.error('Formato inválido — envie um array JSON de itens ou { "items": [...] }');
      return;
    }
    setBulkImportSaving(true);
    try {
      const response = await axios.post(
        `${API}/menu-items/bulk?category=${encodeURIComponent(bulkImportCategory)}`,
        { items }
      );
      const { inserted, errors } = response.data;
      if (inserted > 0) toast.success(`${inserted} itens adicionados`);
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} itens ignorados (ver console)`);
        console.warn('Bulk import errors:', errors);
      }
      setBulkImportCategory(null);
      setBulkImportText('');
      fetchData();
    } catch (error) {
      toast.error('Erro: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setBulkImportSaving(false);
    }
  };

  // Site Content Functions
  const saveSiteContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, siteContent);
      toast.success('Contenu sauvegardé!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Site Settings Functions
  const saveSiteSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, siteSettings);
      toast.success('Paramètres sauvegardés!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Reservations Functions
  const updateReservationStatus = async (id, status) => {
    try {
      await axios.put(`${API}/reservations/${id}/status?status=${status}`);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm('Supprimer cette réservation?')) return;
    try {
      await axios.delete(`${API}/reservations/${id}`);
      toast.success('Réservation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Admin Users Functions
  const createAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/register`, newAdmin);
      toast.success('Administrateur créé!');
      setNewAdmin({ name: '', email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (adminId) => {
    if (!window.confirm('Supprimer cet administrateur?')) return;
    try {
      await axios.delete(`${API}/auth/admins/${adminId}`);
      toast.success('Administrateur supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Gallery Functions
  const addGalleryImage = (category) => {
    const newImage = {
      id: `new-${Date.now()}`,
      url: '',
      category,
      alt_fr: '',
      alt_en: '',
      alt_pt: '',
      sort_order: galleryImages.filter(i => i.category === category).length,
      isNew: true
    };
    setGalleryImages(prev => [...prev, newImage]);
  };

  const updateGalleryImage = (imageId, field, value) => {
    setGalleryImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, [field]: value } : img
    ));
  };

  const saveGalleryImage = async (image) => {
    if (!image.url) {
      toast.error('Veuillez ajouter une image');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        url: image.url,
        category: image.category,
        alt_fr: image.alt_fr || '',
        alt_en: image.alt_en || '',
        alt_pt: image.alt_pt || '',
        sort_order: image.sort_order || 0
      };

      if (image.isNew) {
        await axios.post(`${API}/gallery`, payload);
      } else {
        await axios.put(`${API}/gallery/${image.id}`, payload);
      }
      
      toast.success('Image sauvegardée!');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteGalleryImage = async (imageId, isNew) => {
    if (isNew) {
      setGalleryImages(prev => prev.filter(i => i.id !== imageId));
      return;
    }
    if (!window.confirm('Supprimer cette image?')) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}`);
      toast.success('Image supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Menu Categories Functions
  const createCategory = async () => {
    if (!newCategory.slug || !newCategory.name_fr) {
      toast.error('Veuillez remplir le slug et le nom français');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/menu-categories`, {
        ...newCategory,
        sort_order: menuCategories.length
      });
      toast.success('Catégorie créée!');
      setNewCategory({ slug: '', name_fr: '', name_en: '', name_pt: '' });
      setShowCategoryForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = async (category) => {
    setSaving(true);
    try {
      await axios.put(`${API}/menu-categories/${category.id}`, {
        slug: category.slug,
        name_fr: category.name_fr,
        name_en: category.name_en,
        name_pt: category.name_pt,
        sort_order: category.sort_order,
        is_active: category.is_active
      });
      toast.success('Catégorie mise à jour!');
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie? Les items de cette catégorie doivent être supprimés ou déplacés avant.')) return;
    try {
      await axios.delete(`${API}/menu-categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const moveCategoryUp = async (index) => {
    if (index === 0) return;
    const newCategories = [...menuCategories];
    [newCategories[index], newCategories[index - 1]] = [newCategories[index - 1], newCategories[index]];
    const updates = newCategories.map((cat, i) => ({ id: cat.id, sort_order: i }));
    try {
      await axios.put(`${API}/menu-categories/reorder`, updates);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du réordonnancement');
    }
  };

  const moveCategoryDown = async (index) => {
    if (index === menuCategories.length - 1) return;
    const newCategories = [...menuCategories];
    [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
    const updates = newCategories.map((cat, i) => ({ id: cat.id, sort_order: i }));
    try {
      await axios.put(`${API}/menu-categories/reorder`, updates);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du réordonnancement');
    }
  };

  // Event functions
  const createEvent = async () => {
    if (!newEvent.name || !newEvent.organizer_name || !newEvent.organizer_email || !newEvent.organizer_password) {
      toast.error('Veuillez remplir tous les champs obligatoires (incluant le mot de passe)');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/events`, newEvent);
      toast.success('Événement créé!');
      
      // Show password info
      toast.info(`Mot de passe pour l'organisateur: ${newEvent.organizer_password}`, { duration: 10000 });
      
      setNewEvent({ name: '', num_guests: 10, organizer_name: '', organizer_email: '', organizer_whatsapp: '', organizer_password: '', menu_id: '' });
      setShowEventForm(false);
      fetchData();
      
      // Copy link to clipboard
      const link = `${SITE_URL}/evento/${response.data.link_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Lien copié dans le presse-papiers!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement et tous ses pedidos?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Événement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyEventLink = (linkCode) => {
    const link = `${SITE_URL}/evento/${linkCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié!');
  };

  // Event Menu functions
  const saveEventMenu = async () => {
    if (!eventMenuForm.name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editingEventMenu) {
        await axios.put(`${API}/event-menus/${editingEventMenu}`, eventMenuForm);
        toast.success('Menu mis à jour');
      } else {
        await axios.post(`${API}/event-menus`, eventMenuForm);
        toast.success('Menu créé');
      }
      setShowEventMenuForm(false);
      setEditingEventMenu(null);
      setEventMenuForm({ name: '', items: [] });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  const deleteEventMenu = async (menuId) => {
    if (!window.confirm('Supprimer ce menu?')) return;
    try {
      await axios.delete(`${API}/event-menus/${menuId}`);
      toast.success('Menu supprimé');
      fetchData();
    } catch (error) { toast.error('Erreur'); }
  };

  const addEventMenuItem = () => {
    setEventMenuForm({
      ...eventMenuForm,
      items: [...eventMenuForm.items, { id: crypto.randomUUID(), name_fr: '', name_en: '', name_pt: '', price: 0, category: '', description_fr: '', has_cuisson: false }]
    });
  };

  const updateEventMenuItem = (itemId, field, value) => {
    setEventMenuForm({
      ...eventMenuForm,
      items: eventMenuForm.items.map(i => i.id === itemId ? { ...i, [field]: value } : i)
    });
  };

  const removeEventMenuItem = (itemId) => {
    setEventMenuForm({
      ...eventMenuForm,
      items: eventMenuForm.items.filter(i => i.id !== itemId)
    });
  };

  const sidebarItems = [
    { id: 'weekly', icon: UtensilsCrossed, label: 'Menu Semaine' },
    { id: 'cardapio', icon: BookOpen, label: 'Cardápio Completo' },
    { id: 'categories', icon: FolderPlus, label: 'Catégories Menu' },
    { id: 'events', icon: PartyPopper, label: 'Événements' },
    { id: 'gallery', icon: Images, label: 'Galerie' },
    {
      id: 'content_group',
      icon: FileEdit,
      label: 'Contenu du Site',
      children: [
        { id: 'page_home', icon: FileEdit, label: 'Page Accueil' },
        { id: 'page_about', icon: FileEdit, label: 'Page À Propos' },
        { id: 'page_menu', icon: FileEdit, label: 'Page Carte' },
        { id: 'page_gallery', icon: FileEdit, label: 'Page Galerie' },
        { id: 'page_reservations', icon: FileEdit, label: 'Page Réservations' },
        { id: 'images', icon: Image, label: 'Images du Site' },
      ]
    },
    { id: 'settings', icon: Settings, label: 'Paramètres' },
    { id: 'reservations', icon: Calendar, label: 'Réservations' },
    { id: 'clients', icon: Users, label: 'Clients' },
    { id: 'delivery', icon: Truck, label: 'Livraison' },
    { id: 'users', icon: Users, label: 'Utilisateurs' },
  ];

  // Auto-open "Contenu du Site" group if its child is active
  const contentGroupChildIds = ['page_home', 'page_about', 'page_menu', 'page_gallery', 'page_reservations', 'images'];
  const contentGroupOpen = sidebarGroups.content || contentGroupChildIds.includes(activeTab);

  // Find human label for header (works with grouped items)
  const findLabel = (tab) => {
    for (const item of sidebarItems) {
      if (item.id === tab) return item.label;
      if (item.children) {
        const c = item.children.find(x => x.id === tab);
        if (c) return c.label;
      }
    }
    return '';
  };

  const dishCategories = [
    { id: 'entree', label: 'Entrée' },
    { id: 'meat', label: 'Viande' },
    { id: 'vegetarian', label: 'Végétarien' },
    { id: 'seafood', label: 'Poisson / Fruits de Mer' },
    { id: 'dessert', label: 'Dessert' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#121212] border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="font-display text-2xl text-white">BÉNI</Link>
            <p className="text-xs uppercase tracking-[0.15em] text-[#d4af37] mt-1">Administration</p>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarItems.map(item => {
              if (item.children) {
                const isOpen = contentGroupOpen;
                const hasActiveChild = item.children.some(c => c.id === activeTab);
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => setSidebarGroups(prev => ({ ...prev, content: !contentGroupOpen }))}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                        hasActiveChild ? 'text-[#d4af37]' : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`sidebar-group-${item.id}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {isOpen && (
                      <div className="ml-4 border-l border-white/10 pl-2 mt-1 space-y-1">
                        {item.children.map(child => (
                          <button
                            key={child.id}
                            onClick={() => { setActiveTab(child.id); setSidebarOpen(false); }}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                              activeTab === child.id
                                ? 'bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]'
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                            }`}
                            data-testid={`sidebar-${child.id}`}
                          >
                            <child.icon className="w-4 h-4" />
                            {child.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    activeTab === item.id
                      ? 'bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/10">
            <p className="text-white/50 text-xs mb-3 truncate">{admin?.email}</p>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-red-400 hover:bg-red-400/10 transition-colors">
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-white p-2">
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="font-display text-xl text-white">
                {findLabel(activeTab)}
              </h1>
            </div>
            <Link to="/" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#d4af37] transition-colors">
              <Home className="w-4 h-4" />
              Voir le site
            </Link>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12"><p className="text-white/50">Chargement...</p></div>
          ) : (
            <>
              {/* Weekly Menu Tab */}
              {activeTab === 'weekly' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setWeeklyJsonOpen(true); setWeeklyJsonText(''); }}
                      className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                      data-testid="weekly-json-btn"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1.5" />Adicionar via JSON
                    </Button>
                  </div>
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium">Paramètres du Menu</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.15em] text-white/70">Début de semaine</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start bg-transparent border-white/20 text-white h-12">
                              {format(menuForm.week_start, 'PPP', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10">
                            <CalendarComponent mode="single" selected={menuForm.week_start} onSelect={(date) => date && setMenuForm(prev => ({ ...prev, week_start: date }))} locale={fr} />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.15em] text-white/70">Fin de semaine</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start bg-transparent border-white/20 text-white h-12">
                              {format(menuForm.week_end, 'PPP', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10">
                            <CalendarComponent mode="single" selected={menuForm.week_end} onSelect={(date) => date && setMenuForm(prev => ({ ...prev, week_end: date }))} locale={fr} />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Entrée+Plat+Dessert (€)</Label>
                        <Input type="number" step="0.10" value={menuForm.price_full} onChange={(e) => setMenuForm(prev => ({ ...prev, price_full: parseFloat(e.target.value) }))} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Entrée+Plat (€)</Label>
                        <Input type="number" step="0.10" value={menuForm.price_entree_plat} onChange={(e) => setMenuForm(prev => ({ ...prev, price_entree_plat: parseFloat(e.target.value) }))} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Plat+Dessert (€)</Label>
                        <Input type="number" step="0.10" value={menuForm.price_plat_dessert} onChange={(e) => setMenuForm(prev => ({ ...prev, price_plat_dessert: parseFloat(e.target.value) }))} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Plat Seul (€)</Label>
                        <Input type="number" step="0.10" value={menuForm.price_plat_only} onChange={(e) => setMenuForm(prev => ({ ...prev, price_plat_only: parseFloat(e.target.value) }))} className="bg-transparent border-white/20 text-white" />
                      </div>
                    </div>
                  </div>

                  {dishCategories.map(category => {
                    const categoryDishes = menuForm.dishes.filter(d => d.category === category.id);
                    return (
                      <div key={category.id} className="bg-[#121212] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-white text-lg font-medium">{category.label}</h3>
                          <Button variant="outline" size="sm" onClick={() => addDish(category.id)} className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10">
                            <Plus className="w-4 h-4 mr-2" />Ajouter
                          </Button>
                        </div>
                        {categoryDishes.length === 0 ? (
                          <p className="text-white/30 text-sm">Aucun plat ajouté</p>
                        ) : (
                          <div className="space-y-6">
                            {categoryDishes.map(dish => (
                              <div key={dish.id} className="bg-white/5 p-4 space-y-3">
                                <div className="flex justify-end">
                                  <button onClick={() => removeDish(dish.id)} className="text-red-400 hover:text-red-300 p-1">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <Input value={dish.name_fr} onChange={(e) => updateDish(dish.id, 'name_fr', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-9" placeholder="Nom (FR)" />
                                  <Input value={dish.name_en} onChange={(e) => updateDish(dish.id, 'name_en', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-9" placeholder="Name (EN)" />
                                  <Input value={dish.name_pt} onChange={(e) => updateDish(dish.id, 'name_pt', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-9" placeholder="Nome (PT)" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <Input value={dish.description_fr || ''} onChange={(e) => updateDish(dish.id, 'description_fr', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-8" placeholder="Description (FR)" />
                                  <Input value={dish.description_en || ''} onChange={(e) => updateDish(dish.id, 'description_en', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-8" placeholder="Description (EN)" />
                                  <Input value={dish.description_pt || ''} onChange={(e) => updateDish(dish.id, 'description_pt', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-8" placeholder="Descrição (PT)" />
                                </div>
                                <ImageUpload value={dish.image_url} onChange={(url) => updateDish(dish.id, 'image_url', url)} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end">
                    <Button onClick={saveWeeklyMenu} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white px-8">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Weekly Menu JSON Import Modal */}
              {weeklyJsonOpen && (
                <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setWeeklyJsonOpen(false)}>
                  <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-white text-lg font-medium">Ajouter des plats au Menu de la Semaine — <span className="text-[#d4af37]">via JSON</span></h3>
                      <button onClick={() => setWeeklyJsonOpen(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>

                    <div className="bg-[#121212] border border-white/10 p-3 text-xs text-white/70 space-y-2">
                      <p className="text-white/90 font-medium">Formato esperado (array JSON):</p>
                      <pre className="text-[10px] text-white/60 overflow-x-auto whitespace-pre-wrap">{`[
  {
    "category": "entree",
    "name_fr": "Entrée de la Semaine",
    "name_en": "Weekly Starter",
    "name_pt": "Entrada da Semana",
    "description_fr": "Bruschetta aux figues et burrata"
  },
  { "category": "meat",       "name_fr": "Suggestion Viande",       "description_fr": "Brochette de bœuf, tomates rôties et frites" },
  { "category": "vegetarian", "name_fr": "Suggestion Végétarienne", "description_fr": "Lasagnes aux légumes" },
  { "category": "seafood",    "name_fr": "Suggestion Poisson",      "description_fr": "Conchiglioni aux scampi et épinards" },
  { "category": "dessert",    "name_fr": "Café Gourmand",           "description_fr": "Assortiment de mignardises" },
  { "category": "dessert",    "name_fr": "Fraise Melba",            "description_fr": "Fraise Melba" }
]`}</pre>
                      <p className="text-white/50">Categorias válidas: <code className="text-[#d4af37]">entree</code>, <code className="text-[#d4af37]">meat</code>, <code className="text-[#d4af37]">vegetarian</code>, <code className="text-[#d4af37]">seafood</code>, <code className="text-[#d4af37]">dessert</code>. Se <code className="text-[#d4af37]">name_en</code> / <code className="text-[#d4af37]">name_pt</code> forem omitidos, o <code className="text-[#d4af37]">name_fr</code> é usado. Os pratos são <b>adicionados</b> aos existentes — clique em <b>Enregistrer</b> para salvar.</p>
                    </div>

                    <Textarea
                      value={weeklyJsonText}
                      onChange={(e) => setWeeklyJsonText(e.target.value)}
                      className="bg-[#121212] border-white/20 text-white text-xs font-mono min-h-[240px]"
                      placeholder='[ { "category": "entree", "name_fr": "..." } ]'
                      data-testid="weekly-json-textarea"
                    />

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setWeeklyJsonOpen(false)} className="border-white/20 text-white hover:bg-white/10 h-8 text-xs">Annuler</Button>
                      <Button onClick={applyWeeklyJsonImport} disabled={!weeklyJsonText.trim()} className="bg-[#d4af37] text-black hover:bg-white h-8 text-xs" data-testid="weekly-json-submit-btn">
                        <Plus className="w-3 h-3 mr-1" />Ajouter à la liste
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Cardápio Completo Tab */}
              {activeTab === 'cardapio' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={exportMenuBackup}
                      className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                      data-testid="menu-backup-export-btn"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />Baixar Backup
                    </Button>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="application/json,.json"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          importMenuBackup(f);
                          e.target.value = '';
                        }}
                        data-testid="menu-backup-import-input"
                      />
                      <span
                        className="inline-flex items-center border border-white/20 text-white hover:bg-white/10 h-8 text-xs px-3 rounded-md"
                        data-testid="menu-backup-import-btn"
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />Restaurar Backup
                      </span>
                    </label>
                  </div>
                  {menuCategories.length === 0 ? (
                    <p className="text-white/50 text-center py-8">Nenhuma categoria criada. Vá para "Catégories Menu" para criar.</p>
                  ) : (
                    <Tabs defaultValue={menuCategories[0]?.slug} className="w-full">
                      <TabsList className="bg-[#121212] border border-white/10 p-1 mb-6 flex-wrap">
                        {menuCategories.map(cat => (
                          <TabsTrigger key={cat.id} value={cat.slug} className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black">
                            {cat.name_fr}
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      {menuCategories.map(category => (
                        <TabsContent key={category.id} value={category.slug} className="space-y-2">
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="text-white text-sm font-medium">{category.name_fr}</h3>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setBulkImportCategory(category.slug); setBulkImportText(''); }}
                                className="border-white/20 text-white hover:bg-white/10 h-7 text-xs"
                                data-testid={`bulk-json-btn-${category.slug}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />JSON
                              </Button>
                              <Button size="sm" onClick={() => addMenuItem(category.slug)} className="bg-[#d4af37] text-black hover:bg-white h-7 text-xs">
                                <Plus className="w-3 h-3 mr-1" />Ajouter
                              </Button>
                            </div>
                          </div>

                          {menuItems.filter(item => item.category === category.slug).map(item => (
                            <div key={item.id} className="bg-[#121212] border border-white/10 p-3 space-y-2">
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                <Input value={item.name_fr} onChange={(e) => updateMenuItem(item.id, 'name_fr', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-8" placeholder="Nom (FR)" />
                                <Input value={item.name_en} onChange={(e) => updateMenuItem(item.id, 'name_en', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-8" placeholder="Name (EN)" />
                                <Input value={item.name_pt} onChange={(e) => updateMenuItem(item.id, 'name_pt', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-8" placeholder="Nome (PT)" />
                                <Input type="number" step="0.01" value={item.price} onChange={(e) => updateMenuItem(item.id, 'price', e.target.value)} className="bg-transparent border-white/20 text-white text-sm h-8" placeholder="Prix €" />
                                <select
                                  value={item.category}
                                  onChange={(e) => updateMenuItem(item.id, 'category', e.target.value)}
                                  className="bg-transparent border border-white/20 text-white text-sm h-8 rounded-md px-2"
                                >
                                  {menuCategories.map(cat => (
                                    <option key={cat.slug} value={cat.slug} className="bg-[#121212]">{cat.name_fr}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Input value={item.description_fr || ''} onChange={(e) => updateMenuItem(item.id, 'description_fr', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Description (FR)" />
                                <Input value={item.description_en || ''} onChange={(e) => updateMenuItem(item.id, 'description_en', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Description (EN)" />
                                <Input value={item.description_pt || ''} onChange={(e) => updateMenuItem(item.id, 'description_pt', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Descrição (PT)" />
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1">
                                  <Input
                                    value={item.subcategory || ''}
                                    onChange={(e) => updateMenuItem(item.id, 'subcategory', e.target.value)}
                                    className="bg-transparent border-white/20 text-white text-xs h-7"
                                    placeholder="Sous-catégorie (ex: Apéritifs, Bières, Vins…)"
                                    data-testid={`item-subcategory-${item.id}`}
                                    list={`subcategories-${category.slug}`}
                                  />
                                  <datalist id={`subcategories-${category.slug}`}>
                                    {Array.from(new Set(menuItems.filter(i => i.category === category.slug && i.subcategory).map(i => i.subcategory))).map(sub => (
                                      <option key={sub} value={sub} />
                                    ))}
                                  </datalist>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1">
                                  <Input value={item.image_url || ''} onChange={(e) => updateMenuItem(item.id, 'image_url', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="URL da imagem (opcional)" />
                                </div>
                                <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer select-none whitespace-nowrap" title="Disponible pour la livraison">
                                  <input
                                    type="checkbox"
                                    checked={item.delivery_available !== false}
                                    onChange={(e) => updateMenuItem(item.id, 'delivery_available', e.target.checked)}
                                    className="w-3.5 h-3.5 accent-[#d4af37]"
                                    data-testid={`item-delivery-${item.id}`}
                                  />
                                  🛵 Livraison
                                </label>
                                <Button size="sm" variant="outline" onClick={() => deleteMenuItem(item.id, item.isNew)} className="border-red-500/50 text-red-500 h-7 w-7 p-0">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                                <Button size="sm" onClick={() => saveMenuItem(item)} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white h-7 text-xs px-3">
                                  <Save className="w-3 h-3 mr-1" />Salvar
                                </Button>
                              </div>
                            </div>
                          ))}

                          {menuItems.filter(item => item.category === category.slug).length === 0 && (
                            <p className="text-white/30 text-center py-4 text-sm">Aucun item dans cette catégorie</p>
                          )}
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}
                </motion.div>
              )}

              {/* Bulk JSON Import Modal */}
              {bulkImportCategory && (
                <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => !bulkImportSaving && setBulkImportCategory(null)}>
                  <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-white text-lg font-medium">
                        Adicionar itens via JSON — <span className="text-[#d4af37]">{menuCategories.find(c => c.slug === bulkImportCategory)?.name_fr}</span>
                      </h3>
                      <button onClick={() => setBulkImportCategory(null)} className="text-white/50 hover:text-white" disabled={bulkImportSaving}>
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="bg-[#121212] border border-white/10 p-3 text-xs text-white/70 space-y-2">
                      <p className="text-white/90 font-medium">Formato esperado (array JSON):</p>
                      <pre className="text-[10px] text-white/60 overflow-x-auto whitespace-pre-wrap">{`[
  {
    "name_fr": "Salade César",
    "name_en": "Caesar Salad",
    "name_pt": "Salada César",
    "description_fr": "Salade, poulet, parmesan",
    "description_en": "",
    "description_pt": "",
    "price": 12.5,
    "subcategory": "Salades",
    "delivery_available": true
  },
  { "name_fr": "Tartare", "name_en": "Tartare", "name_pt": "Tartar", "price": 18, "subcategory": "Viandes" }
]`}</pre>
                      <p className="text-white/50">Campos obrigatórios: <code className="text-[#d4af37]">name_fr</code>, <code className="text-[#d4af37]">name_en</code>, <code className="text-[#d4af37]">name_pt</code>, <code className="text-[#d4af37]">price</code>. Opcional: <code className="text-[#d4af37]">subcategory</code> (agrupa itens no site público). A categoria é preenchida automaticamente.</p>
                    </div>

                    <Textarea
                      value={bulkImportText}
                      onChange={(e) => setBulkImportText(e.target.value)}
                      className="bg-[#121212] border-white/20 text-white text-xs font-mono min-h-[240px]"
                      placeholder='[ { "name_fr": "...", "name_en": "...", "name_pt": "...", "price": 0 } ]'
                      data-testid="bulk-json-textarea"
                    />

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setBulkImportCategory(null)} disabled={bulkImportSaving} className="border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                        Annuler
                      </Button>
                      <Button onClick={submitBulkImport} disabled={bulkImportSaving || !bulkImportText.trim()} className="bg-[#d4af37] text-black hover:bg-white h-8 text-xs" data-testid="bulk-json-submit-btn">
                        <Save className="w-3 h-3 mr-1" />{bulkImportSaving ? 'Importation…' : 'Importer'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Management Tab */}
              {activeTab === 'categories' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  {/* Add Category Form */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-white text-lg font-medium">Catégories du Menu</h2>
                      <Button onClick={() => setShowCategoryForm(!showCategoryForm)} className="bg-[#d4af37] text-black hover:bg-white">
                        <Plus className="w-4 h-4 mr-2" />Nouvelle Catégorie
                      </Button>
                    </div>

                    {showCategoryForm && (
                      <div className="bg-white/5 p-4 space-y-4 mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Slug (identifiant unique)</Label>
                            <Input 
                              value={newCategory.slug} 
                              onChange={(e) => setNewCategory({...newCategory, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="ex: viandes" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Nom (FR)</Label>
                            <Input 
                              value={newCategory.name_fr} 
                              onChange={(e) => setNewCategory({...newCategory, name_fr: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Viandes" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Name (EN)</Label>
                            <Input 
                              value={newCategory.name_en} 
                              onChange={(e) => setNewCategory({...newCategory, name_en: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Meats" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Nome (PT)</Label>
                            <Input 
                              value={newCategory.name_pt} 
                              onChange={(e) => setNewCategory({...newCategory, name_pt: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Carnes" 
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowCategoryForm(false)} className="border-white/20 text-white/70">
                            Annuler
                          </Button>
                          <Button onClick={createCategory} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white">
                            <Save className="w-4 h-4 mr-2" />Créer
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Categories List */}
                  <div className="bg-[#121212] border border-white/10 p-6">
                    <h3 className="text-white text-lg font-medium mb-4">Ordre des Catégories (drag pour réordonner)</h3>
                    <div className="space-y-2">
                      {menuCategories.map((category, index) => (
                        <div key={category.id} className="bg-white/5 border border-white/10 p-4 flex items-center gap-4">
                          <div className="flex flex-col gap-1">
                            <button 
                              onClick={() => moveCategoryUp(index)} 
                              disabled={index === 0}
                              className="text-white/50 hover:text-white disabled:opacity-30"
                            >
                              ▲
                            </button>
                            <button 
                              onClick={() => moveCategoryDown(index)} 
                              disabled={index === menuCategories.length - 1}
                              className="text-white/50 hover:text-white disabled:opacity-30"
                            >
                              ▼
                            </button>
                          </div>
                          
                          {editingCategory?.id === category.id ? (
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                              <Input 
                                value={editingCategory.name_fr} 
                                onChange={(e) => setEditingCategory({...editingCategory, name_fr: e.target.value})} 
                                className="bg-transparent border-white/20 text-white text-sm" 
                                placeholder="Nom (FR)" 
                              />
                              <Input 
                                value={editingCategory.name_en} 
                                onChange={(e) => setEditingCategory({...editingCategory, name_en: e.target.value})} 
                                className="bg-transparent border-white/20 text-white text-sm" 
                                placeholder="Name (EN)" 
                              />
                              <Input 
                                value={editingCategory.name_pt} 
                                onChange={(e) => setEditingCategory({...editingCategory, name_pt: e.target.value})} 
                                className="bg-transparent border-white/20 text-white text-sm" 
                                placeholder="Nome (PT)" 
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => updateCategory(editingCategory)} className="bg-[#d4af37] text-black">
                                  <Save className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)} className="border-white/20 text-white/70">
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex-1">
                                <p className="text-white font-medium">{category.name_fr}</p>
                                <p className="text-white/50 text-sm">EN: {category.name_en} | PT: {category.name_pt}</p>
                                <p className="text-white/30 text-xs">slug: {category.slug}</p>
                              </div>
                              <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer select-none whitespace-nowrap" title="Disponible pour la livraison">
                                <input
                                  type="checkbox"
                                  checked={category.delivery_available !== false}
                                  onChange={async (e) => {
                                    const updated = { ...category, delivery_available: e.target.checked };
                                    try {
                                      await axios.put(`${API}/menu-categories/${category.id}`, {
                                        slug: updated.slug,
                                        name_fr: updated.name_fr,
                                        name_en: updated.name_en,
                                        name_pt: updated.name_pt,
                                        sort_order: updated.sort_order || 0,
                                        is_active: updated.is_active !== false,
                                        delivery_available: e.target.checked
                                      });
                                      setMenuCategories(prev => prev.map(c => c.id === category.id ? updated : c));
                                      toast.success(`Catégorie "${category.name_fr}" ${e.target.checked ? 'activée' : 'désactivée'} en livraison`);
                                    } catch (err) {
                                      toast.error('Erreur lors de la mise à jour');
                                    }
                                  }}
                                  className="w-3.5 h-3.5 accent-[#d4af37]"
                                  data-testid={`cat-delivery-${category.id}`}
                                />
                                🛵 Livraison
                              </label>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingCategory({...category})} className="border-[#d4af37] text-[#d4af37]">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteCategory(category.id)} className="border-red-500 text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {menuCategories.length === 0 && (
                      <p className="text-white/30 text-center py-8">Aucune catégorie créée</p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Events Tab */}
              {activeTab === 'events' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  {/* Create Event Form */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-white text-lg font-medium">Événements (Reserva de Pratos em Grupo)</h2>
                      <Button onClick={() => setShowEventForm(!showEventForm)} className="bg-[#d4af37] text-black hover:bg-white">
                        <Plus className="w-4 h-4 mr-2" />Nouvel Événement
                      </Button>
                    </div>

                    {showEventForm && (
                      <div className="bg-white/5 p-6 space-y-4 mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Nom de l'événement *</Label>
                            <Input 
                              value={newEvent.name} 
                              onChange={(e) => setNewEvent({...newEvent, name: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Ex: Anniversaire Juliana" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Nombre de convives</Label>
                            <Input 
                              type="number"
                              value={newEvent.num_guests} 
                              onChange={(e) => setNewEvent({...newEvent, num_guests: parseInt(e.target.value) || 10})} 
                              className="bg-transparent border-white/20 text-white" 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Nom de l'organisateur *</Label>
                            <Input 
                              value={newEvent.organizer_name} 
                              onChange={(e) => setNewEvent({...newEvent, organizer_name: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Juliana Silva" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Email de l'organisateur *</Label>
                            <Input 
                              type="email"
                              value={newEvent.organizer_email} 
                              onChange={(e) => setNewEvent({...newEvent, organizer_email: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="juliana@email.com" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">WhatsApp (optionnel)</Label>
                            <Input 
                              value={newEvent.organizer_whatsapp} 
                              onChange={(e) => setNewEvent({...newEvent, organizer_whatsapp: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="+352 123 456 789" 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Mot de passe pour l'organisateur *</Label>
                            <Input 
                              type="text"
                              value={newEvent.organizer_password} 
                              onChange={(e) => setNewEvent({...newEvent, organizer_password: e.target.value})} 
                              className="bg-transparent border-white/20 text-white" 
                              placeholder="Créer un mot de passe" 
                            />
                            <p className="text-white/40 text-xs">Ce mot de passe sera donné à l'organisateur pour voir et gérer les commandes</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-white/70">Menu de l'événement</Label>
                            <select
                              value={newEvent.menu_id}
                              onChange={(e) => setNewEvent({...newEvent, menu_id: e.target.value})}
                              className="w-full h-10 bg-transparent border border-white/20 text-white rounded-md px-3 text-sm"
                            >
                              <option value="" className="bg-[#121212]">Menu du restaurant (par défaut)</option>
                              {eventMenus.map(m => (
                                <option key={m.id} value={m.id} className="bg-[#121212]">{m.name} ({m.items?.length || 0} items)</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setShowEventForm(false)} className="border-white/20 text-white/70">
                            Annuler
                          </Button>
                          <Button onClick={createEvent} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white">
                            <Save className="w-4 h-4 mr-2" />Créer et Copier le Lien
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Event Menus Management */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h3 className="text-white text-sm font-medium">Menus d'événement</h3>
                      <Button size="sm" onClick={() => { setShowEventMenuForm(true); setEditingEventMenu(null); setEventMenuForm({ name: '', items: [] }); }} className="bg-[#d4af37] text-black hover:bg-white h-7 text-xs">
                        <Plus className="w-3 h-3 mr-1" />Nouveau menu
                      </Button>
                    </div>

                    {eventMenus.length > 0 && (
                      <div className="space-y-2">
                        {eventMenus.map(menu => (
                          <div key={menu.id} className="flex items-center justify-between bg-white/5 p-3 rounded">
                            <div>
                              <span className="text-white text-sm font-medium">{menu.name}</span>
                              <span className="text-white/40 text-xs ml-2">({menu.items?.length || 0} items)</span>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => { setEditingEventMenu(menu.id); setEventMenuForm({ name: menu.name, items: menu.items || [] }); setShowEventMenuForm(true); }} className="border-white/20 text-white text-xs h-7 px-2">
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => deleteEventMenu(menu.id)} className="border-red-500/50 text-red-500 text-xs h-7 px-2">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showEventMenuForm && (
                      <div className="bg-white/5 p-4 space-y-3 mt-2">
                        <Input value={eventMenuForm.name} onChange={(e) => setEventMenuForm({...eventMenuForm, name: e.target.value})} className="bg-transparent border-white/20 text-white text-sm h-8" placeholder="Nom du menu (ex: Menu Noël 2026)" />
                        
                        {eventMenuForm.items.map((item, idx) => (
                          <div key={item.id || idx} className="space-y-1.5 border border-white/10 p-2 rounded">
                            <div className="grid grid-cols-6 gap-2 items-center">
                              <Input value={item.name_fr} onChange={(e) => updateEventMenuItem(item.id, 'name_fr', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7 col-span-2" placeholder="Nom (FR)" />
                              <Input value={item.name_en || ''} onChange={(e) => updateEventMenuItem(item.id, 'name_en', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Name (EN)" />
                              <Input type="number" step="0.01" value={item.price} onChange={(e) => updateEventMenuItem(item.id, 'price', parseFloat(e.target.value) || 0)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Prix" />
                              <Input value={item.category || ''} onChange={(e) => updateEventMenuItem(item.id, 'category', e.target.value)} className="bg-transparent border-white/20 text-white text-xs h-7" placeholder="Catégorie" />
                              <Button size="sm" variant="outline" onClick={() => removeEventMenuItem(item.id)} className="border-red-500/50 text-red-500 h-7 w-7 p-0">×</Button>
                            </div>
                            <label className="flex items-center gap-1.5 text-white/60 text-[11px] cursor-pointer select-none" data-testid={`evtmenu-has-cuisson-${item.id}`}>
                              <input
                                type="checkbox"
                                checked={!!item.has_cuisson}
                                onChange={(e) => updateEventMenuItem(item.id, 'has_cuisson', e.target.checked)}
                                className="w-3.5 h-3.5 accent-[#d4af37]"
                              />
                              Demander la cuisson (Bleu · Saignant · À point · Bien cuit)
                            </label>
                          </div>
                        ))}
                        
                        <div className="flex gap-2">
                          <Button size="sm" onClick={addEventMenuItem} className="bg-white/10 text-white text-xs h-7">+ Item</Button>
                          <Button size="sm" onClick={saveEventMenu} disabled={saving} className="bg-[#d4af37] text-black text-xs h-7 px-3">
                            <Save className="w-3 h-3 mr-1" />{editingEventMenu ? 'Mettre à jour' : 'Créer menu'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setShowEventMenuForm(false); setEditingEventMenu(null); }} className="border-white/20 text-white/50 text-xs h-7">Annuler</Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Events List */}
                  <div className="bg-[#121212] border border-white/10 p-6">
                    <h3 className="text-white text-lg font-medium mb-4">Événements Actifs</h3>

                    {/* Emails de notification (PDF) */}
                    <div className="bg-white/5 border border-white/10 p-4 mb-4 space-y-2" data-testid="event-emails-config">
                      <div>
                        <h4 className="text-white text-sm font-medium">📧 Emails destinataires du PDF</h4>
                        <p className="text-white/50 text-xs">Emails qui reçoivent le PDF quand un organisateur envoie sa commande. Un email par ligne.</p>
                      </div>
                      <div className="space-y-1.5">
                        {(siteSettings?.event_notification_emails || []).map((em, idx) => (
                          <div key={idx} className="flex gap-1.5" data-testid={`event-email-row-${idx}`}>
                            <Input
                              type="email"
                              value={em}
                              onChange={(e) => {
                                const arr = [...(siteSettings?.event_notification_emails || [])];
                                arr[idx] = e.target.value;
                                setSiteSettings({...siteSettings, event_notification_emails: arr});
                              }}
                              onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé')).catch(() => toast.error('Erreur'))}
                              placeholder="cuisine@beni.com"
                              className="bg-transparent border-white/20 text-white text-xs h-8"
                              data-testid={`event-email-input-${idx}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => {
                                const arr = (siteSettings?.event_notification_emails || []).filter((_, i) => i !== idx);
                                const updated = {...siteSettings, event_notification_emails: arr};
                                setSiteSettings(updated);
                                axios.put(`${API}/settings`, updated).then(() => toast.success('Supprimé'));
                              }}
                              className="bg-transparent border border-white/20 text-white/60 hover:text-red-400 hover:border-red-400 h-8 w-8 p-0"
                              data-testid={`event-email-remove-${idx}`}
                            >×</Button>
                          </div>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const arr = [...(siteSettings?.event_notification_emails || []), ''];
                          setSiteSettings({...siteSettings, event_notification_emails: arr});
                        }}
                        className="bg-white/5 border border-white/20 text-white/80 text-xs h-8 hover:bg-white/10"
                        data-testid="event-email-add"
                      >
                        + Ajouter un email
                      </Button>
                    </div>

                    {events.length === 0 ? (
                      <p className="text-white/30 text-center py-8">Aucun événement créé</p>
                    ) : (
                      <div className="space-y-4">
                        {events.map(event => (
                          <div key={event.id} className="bg-white/5 border border-white/10 p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="text-white font-medium text-lg">{event.name}</h4>
                                <p className="text-white/50 text-sm">
                                  Organisateur: {event.organizer_name} ({event.organizer_email})
                                </p>
                                <p className="text-white/50 text-sm">
                                  Convives attendus: {event.num_guests}
                                </p>
                                {event.organizer_password && (
                                  <p className="text-[#d4af37] text-sm">
                                    🔑 Mot de passe: <code className="bg-black/30 px-2 py-0.5 rounded">{event.organizer_password}</code>
                                  </p>
                                )}
                                <div className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                                  event.status === 'sent' ? 'bg-green-500/20 text-green-400' : 
                                  event.status === 'closed' ? 'bg-yellow-500/20 text-yellow-400' : 
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {event.status === 'sent' ? '✓ Envoyé' : 
                                   event.status === 'closed' ? 'Fermé' : 
                                   'Ouvert'}
                                </div>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => copyEventLink(event.link_code)}
                                  className="border-[#d4af37] text-[#d4af37]"
                                >
                                  <Copy className="w-4 h-4 mr-1" />Copier Lien
                                </Button>
                                <a
                                  href={`/evento/${event.link_code}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button size="sm" variant="outline" className="border-white/20 text-white/70">
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </a>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/events/${event.link_code}/pdf`, { responseType: 'blob' });
                                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `event_${event.link_code}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      window.URL.revokeObjectURL(url);
                                    } catch (err) {
                                      toast.error('Erreur lors du téléchargement');
                                    }
                                  }}
                                  className="border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10"
                                  data-testid={`event-pdf-download-${event.link_code}`}
                                >
                                  <Download className="w-4 h-4 mr-1" />PDF
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteEvent(event.id)}
                                  className="border-red-500 text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="bg-black/30 p-2 rounded flex items-center gap-2">
                              <code className="text-[#d4af37] text-sm flex-1 truncate">
                                {SITE_URL}/evento/{event.link_code}
                              </code>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* PAGE CONTENT TABS — per-page editors */}
              {activeTab === 'page_home' && siteContent && (
                <PageContentTabs page="home" content={siteContent} setContent={setSiteContent} onSave={saveSiteContent} saving={saving} />
              )}
              {activeTab === 'page_about' && siteContent && (
                <PageContentTabs page="about" content={siteContent} setContent={setSiteContent} onSave={saveSiteContent} saving={saving} />
              )}
              {activeTab === 'page_menu' && siteContent && (
                <PageContentTabs page="menu" content={siteContent} setContent={setSiteContent} onSave={saveSiteContent} saving={saving} />
              )}
              {activeTab === 'page_gallery' && siteContent && (
                <PageContentTabs page="gallery" content={siteContent} setContent={setSiteContent} onSave={saveSiteContent} saving={saving} />
              )}
              {activeTab === 'page_reservations' && siteContent && (
                <PageContentTabs page="reservations" content={siteContent} setContent={setSiteContent} onSave={saveSiteContent} saving={saving} />
              )}

              {/* Site Images Tab */}
              {activeTab === 'images' && siteContent && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Image Hero (Page d'accueil)</h2>
                    <ImageUpload value={siteContent.hero_image || ''} onChange={(url) => setSiteContent({...siteContent, hero_image: url})} />
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Image du Chef</h2>
                    <ImageUpload value={siteContent.chef_image || ''} onChange={(url) => setSiteContent({...siteContent, chef_image: url})} />
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Images Section À Propos</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Image 1</Label>
                        <ImageUpload value={siteContent.about_image_1 || ''} onChange={(url) => setSiteContent({...siteContent, about_image_1: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Image 2</Label>
                        <ImageUpload value={siteContent.about_image_2 || ''} onChange={(url) => setSiteContent({...siteContent, about_image_2: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Image 3</Label>
                        <ImageUpload value={siteContent.about_image_3 || ''} onChange={(url) => setSiteContent({...siteContent, about_image_3: url})} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Image Fond Réservations</h2>
                    <ImageUpload value={siteContent.reservation_bg_image || ''} onChange={(url) => setSiteContent({...siteContent, reservation_bg_image: url})} />
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Images de Fond des Pages</h2>
                    <p className="text-white/50 text-sm mb-4">Optionnel - Si aucune image n'est définie, la page gardera son style par défaut</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Accueil (Home)</Label>
                        <ImageUpload value={siteContent.bg_home || ''} onChange={(url) => setSiteContent({...siteContent, bg_home: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Notre Histoire (About)</Label>
                        <ImageUpload value={siteContent.bg_about || ''} onChange={(url) => setSiteContent({...siteContent, bg_about: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Menu (Carte)</Label>
                        <ImageUpload value={siteContent.bg_menu || ''} onChange={(url) => setSiteContent({...siteContent, bg_menu: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Galerie</Label>
                        <ImageUpload value={siteContent.bg_gallery || ''} onChange={(url) => setSiteContent({...siteContent, bg_gallery: url})} />
                      </div>
                      <div>
                        <Label className="text-xs text-white/70 mb-2 block">Réservations</Label>
                        <ImageUpload value={siteContent.bg_reservations || ''} onChange={(url) => setSiteContent({...siteContent, bg_reservations: url})} />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveSiteContent} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white px-8">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer les Images'}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && siteSettings && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Informations Générales</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Nom du Restaurant</Label>
                        <Input value={siteSettings.restaurant_name || ''} onChange={(e) => setSiteSettings({...siteSettings, restaurant_name: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Adresse</Label>
                        <Input value={siteSettings.address || ''} onChange={(e) => setSiteSettings({...siteSettings, address: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Téléphone</Label>
                        <Input value={siteSettings.phone || ''} onChange={(e) => setSiteSettings({...siteSettings, phone: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Email</Label>
                        <Input value={siteSettings.email || ''} onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Slogan</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Slogan (FR)</Label>
                        <Input value={siteSettings.tagline_fr || ''} onChange={(e) => setSiteSettings({...siteSettings, tagline_fr: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Tagline (EN)</Label>
                        <Input value={siteSettings.tagline_en || ''} onChange={(e) => setSiteSettings({...siteSettings, tagline_en: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Slogan (PT)</Label>
                        <Input value={siteSettings.tagline_pt || ''} onChange={(e) => setSiteSettings({...siteSettings, tagline_pt: e.target.value})} className="bg-transparent border-white/20 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Horaires</h2>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Horaires (FR)</Label>
                        <Textarea value={siteSettings.hours_fr || ''} onChange={(e) => setSiteSettings({...siteSettings, hours_fr: e.target.value})} className="bg-transparent border-white/20 text-white resize-none" rows={2} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Hours (EN)</Label>
                        <Textarea value={siteSettings.hours_en || ''} onChange={(e) => setSiteSettings({...siteSettings, hours_en: e.target.value})} className="bg-transparent border-white/20 text-white resize-none" rows={2} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Horários (PT)</Label>
                        <Textarea value={siteSettings.hours_pt || ''} onChange={(e) => setSiteSettings({...siteSettings, hours_pt: e.target.value})} className="bg-transparent border-white/20 text-white resize-none" rows={2} />
                      </div>
                    </div>
                  </div>

                  {/* URL Public Site (for ntfy notification links) */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-3">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">URL Publique du Site</h2>
                    <div className="space-y-2">
                      <Label className="text-xs text-white/70">URL publique (utilisée dans les notifications)</Label>
                      <Input
                        value={siteSettings.public_site_url || ''}
                        onChange={(e) => setSiteSettings({...siteSettings, public_site_url: e.target.value})}
                        className="bg-transparent border-white/20 text-white"
                        placeholder="https://beni-restaurant.com"
                        data-testid="admin-public-site-url"
                      />
                      <p className="text-white/40 text-xs">Ex: https://beni-restaurant.com — sans barre oblique finale. Les boutons "Painel Reservas" et "Painel Pedidos" dans les notifications utiliseront cette URL.</p>
                    </div>
                  </div>

                  {/* ntfy Notifications */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-4">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4 flex items-center gap-2">
                      <span>Notifications Push (ntfy.sh)</span>
                    </h2>
                    <div className="bg-[#d4af37]/5 border border-[#d4af37]/20 p-3 rounded text-xs text-white/60 space-y-1">
                      <p className="text-[#d4af37] font-medium">Comment configurer :</p>
                      <ol className="list-decimal ml-4 space-y-0.5">
                        <li>Installez l'app <a href="https://ntfy.sh/" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">ntfy</a> sur votre téléphone (Play Store / App Store)</li>
                        <li>Dans l'app, créez un topic avec un nom unique et secret (ex: <code className="bg-black/40 px-1">beni-pedidos-x7k9p</code>)</li>
                        <li>Saisissez ce même nom dans le champ ci-dessous</li>
                        <li>Activez les notifications — c'est tout ! 📱</li>
                      </ol>
                      <p className="text-white/40 mt-1">💡 Le topic est votre "canal privé" — gardez-le secret, sinon n'importe qui peut s'y abonner.</p>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siteSettings.ntfy_enabled || false}
                        onChange={(e) => {
                          const updated = {...siteSettings, ntfy_enabled: e.target.checked};
                          setSiteSettings(updated);
                          axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                        }}
                        className="w-4 h-4 accent-[#d4af37]"
                        data-testid="admin-ntfy-toggle"
                      />
                      <span className="text-white text-sm font-medium">Activer les notifications push</span>
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Topic ntfy (secret)</Label>
                        <Input
                          value={siteSettings.ntfy_topic || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, ntfy_topic: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white font-mono text-sm"
                          placeholder="beni-pedidos-x7k9p"
                          data-testid="admin-ntfy-topic"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Serveur ntfy (par défaut : ntfy.sh)</Label>
                        <Input
                          value={siteSettings.ntfy_server || 'https://ntfy.sh'}
                          onChange={(e) => setSiteSettings({...siteSettings, ntfy_server: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white font-mono text-sm"
                          placeholder="https://ntfy.sh"
                          data-testid="admin-ntfy-server"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-white/5">
                      <p className="text-white/70 text-xs mb-2">Notifier pour :</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.ntfy_notify_reservations ?? true}
                            onChange={(e) => {
                              const updated = {...siteSettings, ntfy_notify_reservations: e.target.checked};
                              setSiteSettings(updated);
                              axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                            }}
                            className="w-4 h-4 accent-[#d4af37]"
                            data-testid="admin-ntfy-reservations"
                          />
                          <span className="text-white text-sm">Réservations</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.ntfy_notify_delivery ?? true}
                            onChange={(e) => {
                              const updated = {...siteSettings, ntfy_notify_delivery: e.target.checked};
                              setSiteSettings(updated);
                              axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                            }}
                            className="w-4 h-4 accent-[#d4af37]"
                            data-testid="admin-ntfy-delivery"
                          />
                          <span className="text-white text-sm">Commandes Livraison</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.ntfy_notify_events ?? true}
                            onChange={(e) => {
                              const updated = {...siteSettings, ntfy_notify_events: e.target.checked};
                              setSiteSettings(updated);
                              axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                            }}
                            className="w-4 h-4 accent-[#d4af37]"
                            data-testid="admin-ntfy-events"
                          />
                          <span className="text-white text-sm">Événements</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/notifications/test`);
                            if (res.data?.sent) toast.success('Notification de test envoyée ! Vérifiez votre téléphone.');
                            else toast.error(res.data?.error || 'Notification non envoyée. Vérifiez le topic et l\'activation.');
                          } catch (e) {
                            toast.error('Erreur : ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                        className="bg-[#d4af37] text-black hover:bg-white text-sm"
                        data-testid="admin-ntfy-test-btn"
                      >
                        Envoyer une notification de test
                      </Button>
                      <a
                        href={`https://ntfy.sh/${siteSettings.ntfy_topic || 'YOUR_TOPIC'}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#d4af37] text-xs hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Ouvrir le topic dans le navigateur
                      </a>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveSiteSettings} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white px-8">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Enregistrement...' : 'Enregistrer les Paramètres'}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Reservations Tab */}
              {activeTab === 'reservations' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  {/* Reservation Settings */}
                  <div className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={siteSettings?.reservations_enabled ?? true}
                          onChange={(e) => {
                            const updated = {...siteSettings, reservations_enabled: e.target.checked};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }}
                          className="w-4 h-4 accent-[#d4af37]"
                        />
                        <span className="text-white text-sm">Réservations activées</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={siteSettings?.reservations_auto_confirm ?? false}
                          onChange={(e) => {
                            const updated = {...siteSettings, reservations_auto_confirm: e.target.checked};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }}
                          className="w-4 h-4 accent-[#d4af37]"
                        />
                        <span className="text-white text-sm">Confirmation automatique</span>
                      </label>
                    </div>
                    <a href={`${window.location.origin}/painel`} target="_blank" rel="noreferrer" className="text-[#d4af37] text-sm hover:underline flex items-center gap-1" data-testid="admin-link-painel">
                      <ExternalLink className="w-3 h-3" /> Tableau de bord (Réservations + Commandes)
                    </a>
                  </div>

                  {/* Email Configuration */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Configuration email (Resend)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-white/50 text-xs block mb-1">Email de notification (restaurant)</label>
                        <Input
                          value={siteSettings?.notification_email || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, notification_email: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                          placeholder="email@restaurant.com"
                        />
                      </div>
                      <div>
                        <label className="text-white/50 text-xs block mb-1">Email expéditeur (from)</label>
                        <Input
                          value={siteSettings?.sender_email || 'onboarding@resend.dev'}
                          onChange={(e) => setSiteSettings({...siteSettings, sender_email: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                          placeholder="noreply@seudominio.com"
                        />
                      </div>
                    </div>
                    <p className="text-white/30 text-xs">Nota: Em modo teste do Resend, emails só são enviados para endereços verificados na conta.</p>
                  </div>

                  {/* Time Slots — per day + global fallback */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="text-white text-sm font-medium">Horaires de réservation</h4>
                      <p className="text-white/40 text-[11px]">Configurez les horaires <span className="text-[#d4af37]">par jour</span>. Si un jour est vide, les "horaires par défaut" ci-dessous s'appliquent.</p>
                    </div>

                    {/* Per-Day editor */}
                    <div className="space-y-2 border-l-2 border-[#d4af37]/30 pl-3">
                      {['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'].map((dayName, dayIdx) => {
                        const dayKey = String(dayIdx);
                        const hoursByDay = siteSettings?.reservation_hours_by_day || {};
                        const slots = hoursByDay[dayKey] || [];
                        const updateSlots = (newSlots) => {
                          const updated = { ...siteSettings, reservation_hours_by_day: { ...hoursByDay, [dayKey]: newSlots } };
                          setSiteSettings(updated);
                          return updated;
                        };
                        const persist = (updated) => {
                          axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                        };
                        return (
                          <div key={dayIdx} className="flex items-start gap-2 flex-wrap py-1 border-b border-white/5 last:border-0" data-testid={`admin-resv-day-${dayIdx}`}>
                            <div className="w-20 text-white/70 text-xs uppercase tracking-wider shrink-0 pt-1.5">{dayName}</div>
                            <div className="flex-1 flex flex-wrap gap-2 items-center">
                              {slots.length === 0 ? (
                                <span className="text-white/30 text-xs italic">— défaut —</span>
                              ) : slots.map((slot, slotIdx) => (
                                <div key={slotIdx} className="flex items-center gap-1 bg-black/30 px-1.5 py-1 rounded">
                                  <Input
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => {
                                      const ns = [...slots]; ns[slotIdx] = {...ns[slotIdx], start: e.target.value};
                                      updateSlots(ns);
                                    }}
                                    onBlur={() => persist({...siteSettings, reservation_hours_by_day: {...hoursByDay, [dayKey]: slots}})}
                                    className="bg-transparent border-white/20 text-white text-xs h-7 w-24"
                                    data-testid={`admin-resv-day-${dayIdx}-slot-${slotIdx}-start`}
                                  />
                                  <span className="text-white/50 text-xs">à</span>
                                  <Input
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => {
                                      const ns = [...slots]; ns[slotIdx] = {...ns[slotIdx], end: e.target.value};
                                      updateSlots(ns);
                                    }}
                                    onBlur={() => persist({...siteSettings, reservation_hours_by_day: {...hoursByDay, [dayKey]: slots}})}
                                    className="bg-transparent border-white/20 text-white text-xs h-7 w-24"
                                    data-testid={`admin-resv-day-${dayIdx}-slot-${slotIdx}-end`}
                                  />
                                  <button onClick={() => {
                                    const ns = slots.filter((_, i) => i !== slotIdx);
                                    persist(updateSlots(ns));
                                  }} className="text-red-400/70 hover:text-red-400 text-sm px-1" title="Supprimer cette plage">×</button>
                                </div>
                              ))}
                              <Button size="sm" onClick={() => {
                                const ns = [...slots, { start: '11:30', end: '13:30' }];
                                persist(updateSlots(ns));
                              }} className="bg-white/10 text-white text-[11px] h-7 px-2" data-testid={`admin-resv-day-${dayIdx}-add`}>+ Plage</Button>
                              {slots.length > 0 && (
                                <Button size="sm" variant="outline" onClick={() => {
                                  const newHours = { ...hoursByDay };
                                  delete newHours[dayKey];
                                  const updated = { ...siteSettings, reservation_hours_by_day: newHours };
                                  setSiteSettings(updated);
                                  persist(updated);
                                }} className="border-white/20 text-white/40 text-[10px] h-7 px-2" title="Utiliser les horaires par défaut">↺ Défaut</Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Global fallback time slots */}
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-white/50 text-xs mb-2">Horaires par défaut (utilisés pour les jours sans configuration spécifique) :</p>
                      <div className="space-y-2">
                        {(siteSettings?.reservation_time_slots || [{start:'11:30',end:'13:30'},{start:'18:00',end:'20:30'}]).map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={slot.start}
                              onChange={(e) => {
                                const slots = [...(siteSettings?.reservation_time_slots || [])];
                                slots[idx] = {...slots[idx], start: e.target.value};
                                setSiteSettings({...siteSettings, reservation_time_slots: slots});
                              }}
                              className="bg-transparent border-white/20 text-white text-sm h-8 w-28"
                            />
                            <span className="text-white/50 text-sm">à</span>
                            <Input
                              type="time"
                              value={slot.end}
                              onChange={(e) => {
                                const slots = [...(siteSettings?.reservation_time_slots || [])];
                                slots[idx] = {...slots[idx], end: e.target.value};
                                setSiteSettings({...siteSettings, reservation_time_slots: slots});
                              }}
                              className="bg-transparent border-white/20 text-white text-sm h-8 w-28"
                            />
                            <Button size="sm" variant="outline" onClick={() => {
                              const slots = (siteSettings?.reservation_time_slots || []).filter((_, i) => i !== idx);
                              const updated = {...siteSettings, reservation_time_slots: slots};
                              setSiteSettings(updated);
                              axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                            }} className="border-red-500/50 text-red-500 h-7 w-7 p-0 text-xs">×</Button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => {
                            const slots = [...(siteSettings?.reservation_time_slots || []), {start:'18:00', end:'20:30'}];
                            setSiteSettings({...siteSettings, reservation_time_slots: slots});
                          }} className="bg-white/10 text-white text-xs h-7 px-2">+ Ajouter plage par défaut</Button>
                          <Button size="sm" onClick={() => {
                            axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'));
                          }} className="bg-[#d4af37] text-black text-xs h-7 px-3">Sauvegarder tout</Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Closed Weekdays */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Jours de fermeture hebdomadaire</h4>
                    <div className="flex flex-wrap gap-2">
                      {['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'].map((day, idx) => {
                        const closed = siteSettings?.reservation_closed_weekdays || [0];
                        const isClosed = closed.includes(idx);
                        return (
                          <button key={idx} onClick={() => {
                            const newClosed = isClosed ? closed.filter(d => d !== idx) : [...closed, idx];
                            const updated = {...siteSettings, reservation_closed_weekdays: newClosed};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }} className={`px-3 py-1.5 text-xs rounded transition ${isClosed ? 'bg-red-500/30 text-red-300 border border-red-500/50' : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/30'}`}>
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Blocked Specific Dates */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Jours fermés (dates spécifiques)</h4>
                    <div className="flex flex-wrap gap-2">
                      {(siteSettings?.reservation_blocked_dates || []).map((d, idx) => (
                        <span key={idx} className="flex items-center gap-1 bg-red-500/20 text-red-300 px-2 py-1 rounded text-xs">
                          {d}
                          <button onClick={() => {
                            const dates = (siteSettings?.reservation_blocked_dates || []).filter((_, i) => i !== idx);
                            const updated = {...siteSettings, reservation_blocked_dates: dates};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }} className="text-red-400 hover:text-red-200 ml-1">×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        id="blocked-date-input"
                        className="bg-transparent border-white/20 text-white text-sm h-8 w-44"
                      />
                      <Button size="sm" onClick={() => {
                        const input = document.getElementById('blocked-date-input');
                        if (!input.value) return;
                        const dates = [...(siteSettings?.reservation_blocked_dates || []), input.value];
                        const updated = {...siteSettings, reservation_blocked_dates: dates};
                        setSiteSettings(updated);
                        axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                        input.value = '';
                      }} className="bg-[#d4af37] text-black text-xs h-7 px-3">Bloquer</Button>
                    </div>
                  </div>

                  {/* Custom blocked periods (specific date + time range) */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3" data-testid="admin-blocked-periods">
                    <h4 className="text-white text-sm font-medium">Périodes bloquées (date + plage horaire)</h4>
                    {(siteSettings?.reservation_blocked_periods || []).length === 0 ? (
                      <p className="text-white/40 text-xs">Aucune période bloquée.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {(siteSettings?.reservation_blocked_periods || []).map((p, idx) => (
                          <div key={p.id || idx} className="flex items-center gap-2 bg-black/30 px-2 py-1.5 rounded text-xs">
                            <span className="text-[#d4af37] font-medium">{p.date}</span>
                            <span className="text-white/70">{p.start_time}–{p.end_time}</span>
                            {p.label && <span className="text-white/40 italic flex-1 truncate">· {p.label}</span>}
                            <button
                              onClick={() => {
                                const periods = (siteSettings?.reservation_blocked_periods || []).filter((_, i) => i !== idx);
                                const updated = {...siteSettings, reservation_blocked_periods: periods};
                                setSiteSettings(updated);
                                axios.put(`${API}/settings`, updated).then(() => toast.success('Période supprimée'));
                              }}
                              className="text-red-400 hover:text-red-200 ml-auto px-1.5"
                              data-testid={`blocked-period-remove-${idx}`}
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-end gap-2 pt-1">
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1">Date</label>
                        <Input id="bp-date" type="date" className="bg-transparent border-white/20 text-white text-xs h-8 w-40" />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1">Début</label>
                        <Input id="bp-start" type="time" className="bg-transparent border-white/20 text-white text-xs h-8 w-28" />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1">Fin</label>
                        <Input id="bp-end" type="time" className="bg-transparent border-white/20 text-white text-xs h-8 w-28" />
                      </div>
                      <div className="flex-1 min-w-[160px]">
                        <label className="text-white/50 text-[10px] block mb-1">Motif (optionnel)</label>
                        <Input id="bp-label" placeholder="ex: événement privé" className="bg-transparent border-white/20 text-white text-xs h-8" />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const dEl = document.getElementById('bp-date');
                          const sEl = document.getElementById('bp-start');
                          const eEl = document.getElementById('bp-end');
                          const lEl = document.getElementById('bp-label');
                          if (!dEl.value || !sEl.value || !eEl.value) {
                            toast.error('Date, début et fin requis');
                            return;
                          }
                          if (sEl.value >= eEl.value) {
                            toast.error('L\'heure de fin doit être après le début');
                            return;
                          }
                          const newPeriod = {
                            id: `bp-${Date.now()}`,
                            date: dEl.value,
                            start_time: sEl.value,
                            end_time: eEl.value,
                            label: lEl.value || '',
                          };
                          const periods = [...(siteSettings?.reservation_blocked_periods || []), newPeriod];
                          const updated = {...siteSettings, reservation_blocked_periods: periods};
                          setSiteSettings(updated);
                          axios.put(`${API}/settings`, updated).then(() => toast.success('Période ajoutée'));
                          dEl.value = ''; sEl.value = ''; eEl.value = ''; lEl.value = '';
                        }}
                        className="bg-[#d4af37] text-black text-xs h-8 px-3"
                        data-testid="blocked-period-add"
                      >+ Bloquer ce créneau</Button>
                    </div>
                    <p className="text-white/30 text-[11px]">Les réservations dont l'heure tombe dans cette plage seront refusées avec un message d'erreur.</p>
                  </div>

                  {/* Terrace toggle */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3" data-testid="admin-terrace-settings">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <h4 className="text-white text-sm font-medium">Terrasse</h4>
                        <p className="text-white/50 text-xs">Désactivez pour refuser les nouvelles réservations en terrasse.</p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={siteSettings?.terrace_enabled ?? true}
                          onChange={(e) => {
                            const updated = {...siteSettings, terrace_enabled: e.target.checked};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }}
                          className="w-4 h-4 accent-[#d4af37]"
                          data-testid="terrace-enabled-toggle"
                        />
                        <span className="text-white text-sm">{(siteSettings?.terrace_enabled ?? true) ? 'Activée' : 'Désactivée'}</span>
                      </label>
                    </div>
                    {!(siteSettings?.terrace_enabled ?? true) && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1 border-t border-white/5">
                        {['fr', 'en', 'pt'].map((lng) => (
                          <div key={lng}>
                            <label className="text-white/50 text-[10px] block mb-1 uppercase">{lng}</label>
                            <Textarea
                              value={siteSettings?.[`terrace_closed_message_${lng}`] || ''}
                              onChange={(e) => setSiteSettings({...siteSettings, [`terrace_closed_message_${lng}`]: e.target.value})}
                              onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                              className="bg-transparent border-white/20 text-white text-xs resize-none"
                              rows={3}
                              data-testid={`terrace-msg-${lng}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <PopupEventsManager siteSettings={siteSettings} setSiteSettings={setSiteSettings} />


                  {/* Reservation list moved to "Clients" tab */}
                  <div className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="admin-resv-moved-notice">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#d4af37]" />
                      <div>
                        <p className="text-white text-sm font-medium">Liste des réservations déplacée</p>
                        <p className="text-white/50 text-xs">Toutes les réservations sont désormais centralisées dans l'onglet Clients (avec historique complet et export CSV).</p>
                      </div>
                    </div>
                    <Button onClick={() => setActiveTab('clients')} className="bg-[#d4af37] text-black hover:bg-[#d4af37]/90 text-xs h-8 px-3" data-testid="goto-clients-from-resv">
                      Ouvrir Clients →
                    </Button>
                  </div>

                  {/* Feedback Email Config */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Email de feedback (après réservation)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-white/50 text-xs block mb-1">Lien Google Avis</label>
                        <Input
                          value={siteSettings?.google_review_url || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, google_review_url: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                          placeholder="https://g.page/r/..."
                        />
                      </div>
                      <div>
                        <label className="text-white/50 text-xs block mb-1">Délai d'envoi (minutes après réservation)</label>
                        <Input
                          type="number"
                          value={siteSettings?.feedback_delay_minutes || 90}
                          onChange={(e) => setSiteSettings({...siteSettings, feedback_delay_minutes: parseInt(e.target.value) || 90})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8 w-24"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-white/50 text-xs block mb-1">Sujet de l'email</label>
                      <Input
                        value={siteSettings?.feedback_email_subject || 'Merci pour votre visite chez BÉNI !'}
                        onChange={(e) => setSiteSettings({...siteSettings, feedback_email_subject: e.target.value})}
                        onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                        className="bg-transparent border-white/20 text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs block mb-1">Message du feedback</label>
                      <Textarea
                        value={siteSettings?.feedback_email_message || ''}
                        onChange={(e) => setSiteSettings({...siteSettings, feedback_email_message: e.target.value})}
                        onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                        className="bg-transparent border-white/20 text-white text-sm resize-none"
                        rows={3}
                        placeholder="Nous espérons que vous avez passé un agréable moment..."
                      />
                    </div>
                    <p className="text-white/30 text-xs">Le feedback est envoyé automatiquement après le délai configuré. Le lien Google Avis doit être renseigné pour activer cette fonctionnalité.</p>
                  </div>

                  {/* Leads section moved to dedicated "Clients" tab */}
                </motion.div>
              )}

              {/* Clients Tab */}
              {activeTab === 'clients' && (
                <ClientsTab />
              )}

              {/* Delivery Tab */}
              {activeTab === 'delivery' && siteSettings && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  {/* Toggle */}
                  <div className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between flex-wrap gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siteSettings?.delivery_enabled ?? false}
                        onChange={(e) => {
                          const updated = {...siteSettings, delivery_enabled: e.target.checked};
                          setSiteSettings(updated);
                          axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                        }}
                        className="w-4 h-4 accent-[#d4af37]"
                        data-testid="admin-delivery-toggle"
                      />
                      <span className="text-white text-sm">Livraison activée</span>
                    </label>
                    <a href={`${window.location.origin}/livraison`} target="_blank" rel="noreferrer" className="text-[#d4af37] text-sm hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Voir la page Livraison
                    </a>
                  </div>

                  {/* Time Slots */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Horaires de livraison</h4>
                    <div className="space-y-2">
                      {(siteSettings?.delivery_time_slots || [{start:'18:00',end:'20:30'}]).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={slot.start}
                            onChange={(e) => {
                              const slots = [...(siteSettings?.delivery_time_slots || [])];
                              slots[idx] = {...slots[idx], start: e.target.value};
                              setSiteSettings({...siteSettings, delivery_time_slots: slots});
                            }}
                            className="bg-transparent border-white/20 text-white text-sm h-8 w-28"
                          />
                          <span className="text-white/50 text-sm">à</span>
                          <Input
                            type="time"
                            value={slot.end}
                            onChange={(e) => {
                              const slots = [...(siteSettings?.delivery_time_slots || [])];
                              slots[idx] = {...slots[idx], end: e.target.value};
                              setSiteSettings({...siteSettings, delivery_time_slots: slots});
                            }}
                            className="bg-transparent border-white/20 text-white text-sm h-8 w-28"
                          />
                          <Button size="sm" variant="outline" onClick={() => {
                            const slots = (siteSettings?.delivery_time_slots || []).filter((_, i) => i !== idx);
                            const updated = {...siteSettings, delivery_time_slots: slots};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }} className="border-red-500/50 text-red-500 h-7 w-7 p-0 text-xs">×</Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => {
                          const slots = [...(siteSettings?.delivery_time_slots || []), {start:'18:00', end:'20:30'}];
                          setSiteSettings({...siteSettings, delivery_time_slots: slots});
                        }} className="bg-white/10 text-white text-xs h-7 px-2">+ Ajouter plage</Button>
                        <Button size="sm" onClick={() => {
                          axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'));
                        }} className="bg-[#d4af37] text-black text-xs h-7 px-3">Sauvegarder horaires</Button>
                      </div>
                    </div>
                  </div>

                  {/* Closed Weekdays */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Jours fermés (livraison)</h4>
                    <div className="flex flex-wrap gap-2">
                      {['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'].map((day, idx) => {
                        const closed = siteSettings?.delivery_closed_weekdays || [];
                        const isClosed = closed.includes(idx);
                        return (
                          <button key={idx} onClick={() => {
                            const newClosed = isClosed ? closed.filter(d => d !== idx) : [...closed, idx];
                            const updated = {...siteSettings, delivery_closed_weekdays: newClosed};
                            setSiteSettings(updated);
                            axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé'));
                          }} className={`px-3 py-1.5 text-xs rounded transition ${isClosed ? 'bg-red-500/30 text-red-300 border border-red-500/50' : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/30'}`}>
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fees & Minimum */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Frais de livraison & Commande minimum</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-white/50 text-xs">Frais de livraison (€)</label>
                        <Input
                          type="number"
                          step="0.50"
                          min="0"
                          value={siteSettings?.delivery_fee ?? 0}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_fee: parseFloat(e.target.value) || 0})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-9"
                          data-testid="admin-delivery-fee"
                        />
                        <p className="text-white/30 text-[10px]">0 = gratuit</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-white/50 text-xs">Gratuit à partir de (€)</label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={siteSettings?.delivery_free_threshold ?? 0}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_free_threshold: parseFloat(e.target.value) || 0})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-9"
                          data-testid="admin-delivery-free-threshold"
                        />
                        <p className="text-white/30 text-[10px]">0 = jamais offert</p>
                      </div>
                      <div className="space-y-1">
                        <label className="text-white/50 text-xs">Commande minimum (€)</label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={siteSettings?.delivery_min_order ?? 0}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_min_order: parseFloat(e.target.value) || 0})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-9"
                          data-testid="admin-delivery-min-order"
                        />
                        <p className="text-white/30 text-[10px]">0 = pas de minimum</p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="bg-[#121212] border border-white/10 p-4 space-y-3">
                    <h4 className="text-white text-sm font-medium">Message d'horaires (affiché aux clients)</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="text-white/50 text-xs block mb-1">FR</label>
                        <Input
                          value={siteSettings?.delivery_message_fr || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_message_fr: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                        />
                      </div>
                      <div>
                        <label className="text-white/50 text-xs block mb-1">EN</label>
                        <Input
                          value={siteSettings?.delivery_message_en || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_message_en: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                        />
                      </div>
                      <div>
                        <label className="text-white/50 text-xs block mb-1">PT</label>
                        <Input
                          value={siteSettings?.delivery_message_pt || ''}
                          onChange={(e) => setSiteSettings({...siteSettings, delivery_message_pt: e.target.value})}
                          onBlur={() => axios.put(`${API}/settings`, siteSettings).then(() => toast.success('Sauvegardé'))}
                          className="bg-transparent border-white/20 text-white text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delivery orders list moved to "Clients" tab */}
                  <div className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="admin-delivery-moved-notice">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#d4af37]" />
                      <div>
                        <p className="text-white text-sm font-medium">Liste des commandes déplacée</p>
                        <p className="text-white/50 text-xs">Toutes les commandes Livraison sont désormais centralisées dans l'onglet Clients (historique complet par client + export CSV).</p>
                      </div>
                    </div>
                    <Button onClick={() => setActiveTab('clients')} className="bg-[#d4af37] text-black hover:bg-[#d4af37]/90 text-xs h-8 px-3" data-testid="goto-clients-from-delivery">
                      Ouvrir Clients →
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Gallery Tab */}
              {activeTab === 'gallery' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  <Tabs defaultValue="ambiance" className="w-full">
                    <TabsList className="bg-[#121212] border border-white/10 p-1 mb-6">
                      <TabsTrigger value="ambiance" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black">Ambiance</TabsTrigger>
                      <TabsTrigger value="dishes" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black">Plats</TabsTrigger>
                      <TabsTrigger value="team" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black">Équipe</TabsTrigger>
                    </TabsList>

                    {['ambiance', 'dishes', 'team'].map(category => (
                      <TabsContent key={category} value={category} className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-white text-lg capitalize">{category === 'ambiance' ? 'Ambiance' : category === 'dishes' ? 'Plats' : 'Équipe'}</h3>
                          <Button onClick={() => addGalleryImage(category)} className="bg-[#d4af37] text-black hover:bg-white">
                            <Plus className="w-4 h-4 mr-2" />Ajouter
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {galleryImages.filter(img => img.category === category).map(image => (
                            <div key={image.id} className="bg-[#121212] border border-white/10 p-4 space-y-4" data-testid={`gallery-item-${image.id}`}>
                              <ImageUpload value={image.url || ''} onChange={(url) => updateGalleryImage(image.id, 'url', url)} />
                              <div className="grid grid-cols-3 gap-2">
                                <Input 
                                  value={image.alt_fr || ''} 
                                  onChange={(e) => updateGalleryImage(image.id, 'alt_fr', e.target.value)} 
                                  className="bg-transparent border-white/20 text-white text-xs" 
                                  placeholder="Alt (FR)" 
                                />
                                <Input 
                                  value={image.alt_en || ''} 
                                  onChange={(e) => updateGalleryImage(image.id, 'alt_en', e.target.value)} 
                                  className="bg-transparent border-white/20 text-white text-xs" 
                                  placeholder="Alt (EN)" 
                                />
                                <Input 
                                  value={image.alt_pt || ''} 
                                  onChange={(e) => updateGalleryImage(image.id, 'alt_pt', e.target.value)} 
                                  className="bg-transparent border-white/20 text-white text-xs" 
                                  placeholder="Alt (PT)" 
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => deleteGalleryImage(image.id, image.isNew)} className="border-red-500 text-red-500 hover:bg-red-500/10">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                                <Button size="sm" onClick={() => saveGalleryImage(image)} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white">
                                  <Save className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {galleryImages.filter(img => img.category === category).length === 0 && (
                          <p className="text-white/30 text-center py-8">Aucune image dans cette catégorie</p>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </motion.div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                  {/* Create New Admin */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-6">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Créer un Administrateur</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Nom</Label>
                        <Input
                          value={newAdmin.name}
                          onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                          className="bg-transparent border-white/20 text-white"
                          placeholder="Nom complet"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Identifiant</Label>
                        <Input
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                          className="bg-transparent border-white/20 text-white"
                          placeholder="Identifiant de connexion"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-white/70">Mot de passe</Label>
                        <Input
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                          className="bg-transparent border-white/20 text-white"
                          placeholder="Mot de passe sécurisé"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={createAdmin} disabled={saving} className="bg-[#d4af37] text-black hover:bg-white">
                        <Plus className="w-4 h-4 mr-2" />Créer
                      </Button>
                    </div>
                  </div>

                  {/* List of Admins */}
                  <div className="bg-[#121212] border border-white/10 p-6 space-y-4">
                    <h2 className="text-white text-lg font-medium border-b border-white/10 pb-4">Administrateurs ({admins.length})</h2>
                    {admins.map(adminUser => (
                      <div key={adminUser.id} className="flex items-center justify-between p-4 bg-white/5 border border-white/10">
                        <div>
                          <p className="text-white font-medium">{adminUser.name}</p>
                          <p className="text-white/50 text-sm">{adminUser.email}</p>
                          <p className="text-white/30 text-xs">Créé le {new Date(adminUser.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        {adminUser.id !== admin?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteAdmin(adminUser.id)}
                            className="border-red-500 text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {adminUser.id === admin?.id && (
                          <span className="text-xs text-[#d4af37] uppercase tracking-wider">Vous</span>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
